import { readableInk } from '@codezo/brand-detail';
import type { ReactNode } from 'react';
import type { ClientBrandCard } from '../types';
import { Badge, Card, EmptyState, PaletteBar, SectionHead, cn } from '../ui/primitives';

/**
 * The client's brand kits, as cards.
 *
 * ── Why cards that link out, and not a tab ──
 *
 * A brand kit is its own document with its own page. On the v1 client page it
 * was a tab whose open brand lived in component state, which meant a reload
 * dropped you back on the list and there was no URL to send anyone. Here the
 * card links to the brand's own route: refresh works because there is nothing
 * to restore, and the link is shareable because it is a link.
 *
 * The card leads with the palette because that is what identifies a kit at a
 * glance — faster than its name, which is usually a variation on the client's.
 * It shows the WHOLE palette for the reason the brand hero settled on: any rule
 * for choosing a subset was wrong on some real kit.
 */
export function ClientBrands({
	brands,
	meta,
	action,
	emptyHint,
	emptyAction,
	brandHref,
	onBrandClick,
	renderBrandAction,
}: {
	brands: ClientBrandCard[];
	meta?: ReactNode;
	action?: ReactNode;
	emptyHint?: string;
	emptyAction?: ReactNode;
	/** Renders the card as a real anchor, so it opens in a new tab properly. */
	brandHref?: (brand: ClientBrandCard) => string | undefined;
	onBrandClick?: (brand: ClientBrandCard) => void;
	/** The card's own footer control — "Open", a menu. The app's, because the
	 * app owns permissions and what the control does. */
	renderBrandAction?: (brand: ClientBrandCard) => ReactNode;
}) {
	if (brands.length === 0) {
		return (
			<>
				<SectionHead title="Brands" meta={meta} action={action} />
				<EmptyState
					title="No brand kits yet"
					hint={
						emptyHint ??
						'A brand kit holds the logos, colors and type a project inherits.'
					}
					action={emptyAction}
				/>
			</>
		);
	}

	return (
		<>
			<SectionHead title="Brands" meta={meta} action={action} />
			<div className="grid grid-cols-[repeat(auto-fill,minmax(15.5rem,1fr))] gap-3.5">
				{brands.map((brand) => {
					const href = brandHref?.(brand);
					const Tag = href ? 'a' : 'div';
					const interactive = Boolean(href || onBrandClick);

					return (
						<Card key={brand.id} className="flex min-w-0 flex-col">
							<PaletteBar colors={brand.colors} />

							<Tag
								href={href}
								onClick={onBrandClick ? () => onBrandClick(brand) : undefined}
								className={
									interactive
										? 'block cursor-pointer px-4 pt-3.5 no-underline'
										: 'block px-4 pt-3.5'
								}
							>
								<span className="flex items-center gap-2.5">
									{/* Fill and ink are a pair: a brand with no palette
									    gets a plain surface AND the surface's own text
									    colour. Taking `--accent-fg` for the ink while
									    falling back to `--surface-fill` for the fill —
									    which this did — writes near-white on cream. */}
									<span
										className={cn(
											'flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--r-2)] font-mono text-2xs',
											!brand.colors?.[0] && 'bg-stripes text-text-2'
										)}
										style={
											brand.colors?.[0]
												? {
														background: brand.colors[0],
														color: readableInk(brand.colors[0]),
													}
												: undefined
										}
									>
										{brand.code}
									</span>
									<span className="min-w-0 flex-1">
										<span className="block truncate text-xs font-medium text-text-1">
											{brand.name}
										</span>
										{brand.kind && (
											<span className="mt-px block truncate text-2xs text-text-4">
												{brand.kind}
											</span>
										)}
									</span>
									{brand.status && (
										<Badge tone={brand.status.tone} dot>
											{brand.status.label}
										</Badge>
									)}
								</span>
							</Tag>

							<div className="flex flex-col gap-1.5 px-4 py-3.5 text-2xs">
								{[
									['Fonts', brand.fonts?.length ? brand.fonts.join(' · ') : '—'],
									[
										'Files',
										brand.fileCount != null
											? `${brand.fileCount} file${brand.fileCount === 1 ? '' : 's'}`
											: '—',
									],
									['Updated', brand.updatedLabel || '—'],
								].map(([label, value]) => (
									<div key={label} className="flex min-w-0 gap-2.5">
										<span className="w-12 shrink-0 text-text-4">{label}</span>
										<span className="truncate text-text-2">{value}</span>
									</div>
								))}
							</div>

							{renderBrandAction && (
								<div className="mt-auto flex items-center justify-end gap-2 border-t border-line-1 px-3 py-2.5">
									{renderBrandAction(brand)}
								</div>
							)}
						</Card>
					);
				})}
			</div>
		</>
	);
}
